import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Row } from "@tanstack/react-table";
import {
  type Case,
  type Client,
  type DocumentDetail,
  DocumentStatus,
  DocumentSubType,
} from "@/briefpoint-client";
import { TableCell, TableRow } from "@/components/ui/table";
import { useDocumentApi } from "@/hooks/useApi";
import {
  getUnarchivedCaseDocumentListOptions,
  getUnarchivedDocumentListOptions,
} from "@/lib/query-options";
import type { DocumentClient, ViewCommunicationResponse } from "@/lib/types";
import { toLocaleUSDateString } from "@/lib/utils";
import DocumentActionsMenu from "../DocumentList/DocumentActionsMenu";
import { EditableText } from "../EditableText/EditableText";
import { UserNotificationIndicator } from "../UserNotificationIndicator";
import { toast } from "../ui/toast";
import { DocumentAction } from "./DocumentAction";
import styles from "./DocumentListTableSupplementalRow.module.scss";
import { DocumentListTableRowReview } from "./DocumentListTableRowReview";
import useConfig, { CLIENT_TASKS_FF } from "@/hooks/useConfig";
import { useAuth } from "@/hooks/useAuth";
import { useHistory } from "react-router-dom";
import DocumentListTableRowCommunication from "./DocumentListTableRowCommunication";

type Props = {
  row: Row<DocumentDetail>; // non-supplemental document
  document: DocumentClient; // supplemental document
  client?: Client;
  case?: Case;
  sendReminder?: () => void;
  setSelectedClient?: (client: Client) => void;
  selectedClient?: Client;
  setShowEmailModal?: (show: boolean) => void;
  showEmailModal?: boolean;
  communications?: ViewCommunicationResponse[];
  communication?: ViewCommunicationResponse;
  archived?: boolean;
  reviewExpanded?: boolean;
  setReviewExpanded?: (expanded: boolean) => void;
};

export default function DocumentListTableSupplementalRow(props: Props) {
  const userNotificationCount =
    props.document.interrogatoryNotificationCount ?? 0;
  const documentApi = useDocumentApi();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { user, firm } = useAuth();
  const [, featureFlags] = useConfig();

  const useClientTasks = featureFlags()[CLIENT_TASKS_FF]
    ? featureFlags()[CLIENT_TASKS_FF](user, firm)
    : false;
  const updateDocumentMutation = useMutation({
    mutationFn: async ({
      document,
      name,
    }: {
      document: DocumentClient;
      name: string;
    }) => {
      await documentApi.documentUpdateDocument({
        documentId: document.id,
        document: { ...document, name },
      });
      return { document, name };
    },
    onSuccess: ({ name }: { name: string }) => {
      toast.success(`${name} was updated successfully.`);
    },
    onError: ({ name }: { name: string }) => {
      toast.error(`Error updating ${name}. Please try again later.`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getUnarchivedDocumentListOptions(documentApi),
      });
      if (props.case) {
        queryClient.invalidateQueries({
          ...getUnarchivedCaseDocumentListOptions(documentApi, props.case.id),
        });
      }
    },
  });

  const isRFPWithClientTasks =
    useClientTasks &&
    props.document.info?.documentSubType ===
      DocumentSubType.RequestsForProduction;
  return (
    <>
      <TableRow
        key={props.document?.id}
        data-state={props.row.getIsSelected() && "selected"}
      >
        <TableCell colSpan={props.archived ? 4 : 7} className={styles.cellData}>
          <div
            data-testid="supplemental-name-container"
            className={styles.supplementalNameContainer}
          >
            <div className={styles.curveGraphic} />
            <div className={styles.userNotificationContainer}>
              {userNotificationCount > 0 && (
                <UserNotificationIndicator
                  count={userNotificationCount}
                  isSupplement
                />
              )}
            </div>
            <div
              data-testid="supplemental-name"
              className={styles.supplementalName}
              style={userNotificationCount > 0 ? { right: "8px" } : undefined}
            >
              <EditableText
                value={props.document.name}
                onChange={(name) =>
                  updateDocumentMutation.mutate({
                    document: props.document,
                    name,
                  })
                }
              />
            </div>
          </div>
        </TableCell>
        <TableCell colSpan={props.archived ? 1 : 3} className={styles.cellData}>
          <div className={styles.supplementalUploadDate}>
            {props.document?.uploadDate
              ? toLocaleUSDateString(props.document?.uploadDate)
              : "-"}
          </div>
        </TableCell>
        <TableCell colSpan={1} className={styles.cellData}>
          <div className="px-1">
            <DocumentListTableRowCommunication
              setSelectedClient={(client) =>
                props.setSelectedClient?.(client as Client)
              }
              selectedClient={props.selectedClient}
              setShowEmailModal={(show) => props.setShowEmailModal?.(show)}
              showEmailModal={props.showEmailModal ?? false}
              communication={props.communication}
              document={props.document}
              client={props.client}
              reviewExpanded={props.reviewExpanded ?? false}
              setReviewExpanded={() => {
                props.setReviewExpanded?.(true);
              }}
            />
          </div>
        </TableCell>
        <TableCell className={styles.cellData}>
          <div className="d-flex justify-content-end gap-2 align-items-center">
            {!props.archived && (
              <DocumentAction document={props.document as DocumentClient} />
            )}
            {!props.archived &&
              (props.document?.status ?? 0) >= DocumentStatus.Processed && (
                <DocumentActionsMenu
                  document={props.document}
                  client={props.client}
                  _case={props.case}
                  sendReminder={props.sendReminder}
                  setSelectedClient={props.setSelectedClient}
                  communication={props.communication}
                />
              )}
          </div>
        </TableCell>
      </TableRow>
      {props.reviewExpanded && !!props.communication && (
        <TableRow>
          <TableCell
            colSpan={props.row.getVisibleCells().length}
            className="p-0"
          >
            <DocumentListTableRowReview
              communication={props.communication}
              createReview={() => {
                if (isRFPWithClientTasks) {
                  history.push(`/wizard/${props.document.id}`);
                } else {
                  history.push(
                    `/documents/${props.document.id}/client-feedback`,
                  );
                }
              }}
              client={props.client}
              closeReview={() => {
                props.setReviewExpanded?.(false);
              }}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
